import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, ApiError, type Setup } from "../api";
import Chart, { type ChartOverlay } from "../components/Chart";

const STEP_LABELS = ["spike start", "pivot", "range end"] as const;

export default function CreateView() {
  const qc = useQueryClient();
  const navigate = useNavigate();

  const universeQ = useQuery({
    queryKey: ["universe"],
    queryFn: () => api.universe(),
  });

  const [searchParams, setSearchParams] = useSearchParams();
  const urlSymbol = (searchParams.get("symbol") ?? "").toUpperCase();
  const urlTf = searchParams.get("tf") ?? "";

  const [tf, setTfState] = useState<string>(urlTf || "daily");
  const [symbol, setSymbolState] = useState<string>(urlSymbol);
  const [slots, setSlots] = useState<(number | null)[]>([null, null, null]);
  const [direction, setDirection] = useState<"bull" | "bear" | null>(null);
  // The bar currently focused. Desktop: sticky on click (mouse-leave shouldn't
  // clear it). Mobile: follows the crosshair, which a tap pins.
  const [focusedIdx, setFocusedIdx] = useState<number | null>(null);
  const isDesktop = useMemo(
    () =>
      typeof window !== "undefined" &&
      window.matchMedia("(hover: hover) and (pointer: fine)").matches,
    [],
  );

  // Reflect (symbol, tf) in the URL so /create?symbol=AAPL&tf=daily deep-links.
  const syncUrl = (nextSymbol: string, nextTf: string) => {
    const next = new URLSearchParams(searchParams);
    if (nextSymbol) next.set("symbol", nextSymbol);
    else next.delete("symbol");
    if (nextTf) next.set("tf", nextTf);
    else next.delete("tf");
    setSearchParams(next, { replace: true });
  };
  const setSymbol = (s: string) => {
    setSymbolState(s);
    syncUrl(s, tf);
  };
  const setTf = (t: string) => {
    setTfState(t);
    syncUrl(symbol, t);
  };

  // Pull subsequent URL changes (back/forward, external links) back into state.
  useEffect(() => {
    if (urlSymbol && urlSymbol !== symbol) setSymbolState(urlSymbol);
    if (urlTf && urlTf !== tf) setTfState(urlTf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlSymbol, urlTf]);

  const anchors = useMemo(
    () => (slots.every((x) => x !== null) ? (slots as number[]) : []),
    [slots],
  );

  // First-load default: pick `daily` (or first available tf). The symbol
  // picker is now a typeahead backed by Alpaca's full asset universe, so we
  // don't preselect a symbol — the user types one in.
  useEffect(() => {
    const u = universeQ.data?.universe;
    if (!u) return;
    const tfs = Object.keys(u).sort();
    if (tfs.length === 0) return;
    const nextTf = tfs.includes("daily") ? "daily" : tfs[0];
    if (!tf || !tfs.includes(tf)) setTfState(nextTf);
  }, [universeQ.data]);

  const barsQ = useQuery({
    queryKey: ["bars", symbol, tf],
    queryFn: () => api.bars(symbol, tf),
    enabled: !!symbol && !!tf,
  });

  // Reset anchors whenever the (symbol, tf) pair changes.
  useEffect(() => {
    setSlots([null, null, null]);
    setDirection(null);
  }, [symbol, tf]);

  const previewQ = useQuery({
    queryKey: ["setup-preview", symbol, tf, anchors, direction],
    queryFn: () =>
      api.previewSetup({
        symbol,
        tf,
        anchors: anchors as [number, number, number],
        direction: direction ?? undefined,
      }),
    enabled: anchors.length === 3,
    retry: false,
    staleTime: 0,
  });

  // When the server infers a direction, surface it so the user can override.
  useEffect(() => {
    if (previewQ.data && direction === null) {
      setDirection(previewQ.data.direction);
    }
  }, [previewQ.data, direction]);

  const overlay = useMemo<ChartOverlay | undefined>(() => {
    if (anchors.length === 3 && previewQ.data) {
      const s = previewQ.data.setup;
      return {
        spike: [Number(s.leg_start_idx), Number(s.leg_end_idx)],
        range: [Number(s.tr_start_idx), Number(s.tr_end_idx)],
        tr_high: Number(s.tr_high),
        tr_low: Number(s.tr_low),
        target: Number(s.measured_move_target),
      };
    }
    // While capturing anchors: show partial bands so the user gets
    // immediate feedback after each placement.
    const [a, b, c] = slots;
    const partial: ChartOverlay = {};
    if (a !== null && b !== null) partial.spike = [a, b];
    if (b !== null && c !== null) partial.range = [b, c];
    return partial.spike || partial.range ? partial : undefined;
  }, [slots, anchors, previewQ.data]);

  const saveMut = useMutation({
    mutationFn: () =>
      api.createSetup({
        symbol,
        tf,
        anchors: anchors as [number, number, number],
        direction: direction ?? undefined,
      }),
    onSuccess: (resp) => {
      qc.invalidateQueries({ queryKey: ["setups"] });
      const s = resp.setup;
      const params = new URLSearchParams({
        symbol: s.symbol,
        tf: String(s.timeframe ?? tf),
        leg_start_date: String(s.leg_start_date ?? ""),
        tr_end_date: String(s.tr_end_date ?? ""),
      });
      if (s.setup_id) params.set("setup_id", String(s.setup_id));
      // Pass the freshly-created setup via navigation state so SetupDetail
      // can render immediately, without waiting on the setups list refetch.
      navigate(`/detail?${params.toString()}`, { state: { setup: s } });
    },
  });

  const u = universeQ.data?.universe;
  // Create supports any TF the live-fetch fallback handles, not just the
  // ones with parquet files. Union the canonical list with whatever the
  // universe exposes so newly-added parquet TFs still surface.
  const tfs = useMemo(() => {
    const canonical = ["1min", "5min", "30min", "1hour", "4hour", "daily", "weekly", "monthly"];
    const set = new Set<string>(canonical);
    if (u) for (const k of Object.keys(u)) set.add(k);
    return Array.from(set).sort(compareTf);
  }, [u]);

  const placeFocused = (i: number) => {
    if (focusedIdx === null) return;
    setSlots((cur) => {
      const next = [...cur];
      next[i] = focusedIdx;
      return next;
    });
  };

  // Desktop: a candle click fills the first empty slot.
  const fillNext = (idx: number) =>
    setSlots((cur) => {
      const empty = cur.findIndex((x) => x === null);
      if (empty < 0) return cur;
      const next = [...cur];
      next[empty] = idx;
      return next;
    });

  // Desktop: a pill click clears that slot so the next candle-click refills it.
  const clearSlot = (i: number) =>
    setSlots((cur) => {
      if (cur[i] === null) return cur;
      const next = [...cur];
      next[i] = null;
      return next;
    });

  const undo = () =>
    setSlots((cur) => {
      const next = [...cur];
      for (let i = next.length - 1; i >= 0; i--) {
        if (next[i] !== null) {
          next[i] = null;
          break;
        }
      }
      return next;
    });
  const reset = () => {
    setSlots([null, null, null]);
    setDirection(null);
  };

  const previewError =
    previewQ.error instanceof ApiError ? previewQ.error.message : null;
  const saveError =
    saveMut.error instanceof ApiError ? saveMut.error.message : null;

  const navSlot =
    typeof document !== "undefined" ? document.getElementById("nav-slot") : null;
  const pickers = (
    <div className="nav-pickers">
      <SymbolTypeahead
        value={symbol}
        onChange={(s) => {
          setSymbol(s);
          setSlots([null, null, null]);
          setDirection(null);
        }}
      />
    </div>
  );

  return (
    <div>
      {navSlot && createPortal(pickers, navSlot)}

      {!symbol && (
        <p className="muted">Search a symbol above to begin.</p>
      )}
      {symbol && barsQ.isLoading && <p className="muted">Loading bars…</p>}
      {symbol && barsQ.error && (
        <p className="muted">Failed to load bars for {symbol}/{tf}.</p>
      )}
      {symbol && tfs.length > 0 && (
        <TimeframeBar tfs={tfs} value={tf} onChange={setTf} />
      )}
      {barsQ.data && (
        <Chart
          bars={barsQ.data}
          overlay={overlay}
          height={420}
          reserve={210}
          {...(isDesktop
            ? { onBarTap: fillNext }
            : { onCrosshairMove: setFocusedIdx })}
        />
      )}

      <AnchorReadout
        bars={barsQ.data?.t ?? []}
        slots={slots}
        focusedIdx={focusedIdx}
        isDesktop={isDesktop}
        onPlace={placeFocused}
        onClear={clearSlot}
        onUndo={undo}
        onReset={reset}
      />

      {anchors.length === 3 && (
        <DirectionRow
          value={direction}
          inferred={previewQ.data?.direction ?? null}
          onChange={(v) => setDirection(v)}
        />
      )}

      {previewQ.isFetching && <p className="muted small">Computing preview…</p>}
      {previewError && <p className="error">Preview failed: {previewError}</p>}

      {anchors.length === 3 && previewQ.data && (
        <PreviewMetrics setup={previewQ.data.setup} />
      )}

      <div className="action-footer">
        <button
          className="primary"
          disabled={anchors.length !== 3 || !previewQ.data || saveMut.isPending}
          onClick={() => saveMut.mutate()}
          style={{ flex: 1 }}
        >
          {saveMut.isPending ? "Saving…" : "Save manual setup"}
        </button>
      </div>
      {saveError && <p className="error">Save failed: {saveError}</p>}
    </div>
  );
}

function TimeframeBar({
  tfs,
  value,
  onChange,
}: {
  tfs: string[];
  value: string;
  onChange: (tf: string) => void;
}) {
  return (
    <div className="tf-bar">
      {tfs.map((t) => (
        <button
          key={t}
          className={"tf-btn" + (t === value ? " on" : "")}
          onClick={() => onChange(t)}
        >
          {tfLabel(t)}
        </button>
      ))}
    </div>
  );
}

function tfMinutes(tf: string): number {
  const m = tf.match(/^(\d+)\s*(min|minute|hour|h|d|day|w|week|mo|month)/i);
  if (m) {
    const n = Number(m[1]);
    const unit = m[2].toLowerCase();
    if (unit.startsWith("min")) return n;
    if (unit.startsWith("h")) return n * 60;
    if (unit.startsWith("d")) return n * 60 * 24;
    if (unit.startsWith("w")) return n * 60 * 24 * 7;
    if (unit.startsWith("mo")) return n * 60 * 24 * 30;
  }
  if (/^hourly$/i.test(tf)) return 60;
  if (/^daily$/i.test(tf)) return 60 * 24;
  if (/^weekly$/i.test(tf)) return 60 * 24 * 7;
  if (/^monthly$/i.test(tf)) return 60 * 24 * 30;
  return Number.MAX_SAFE_INTEGER;
}

function compareTf(a: string, b: string): number {
  return tfMinutes(a) - tfMinutes(b);
}

function tfLabel(tf: string): string {
  const m = tf.match(/^(\d+)\s*(min|minute|hour|h|d|day|w|week|mo|month)/i);
  if (m) {
    const n = m[1];
    const unit = m[2].toLowerCase();
    if (unit.startsWith("min")) return `${n}m`;
    if (unit.startsWith("h")) return `${n}H`;
    if (unit.startsWith("d")) return `${n}D`;
    if (unit.startsWith("w")) return `${n}W`;
    if (unit.startsWith("mo")) return `${n}M`;
  }
  if (/^daily$/i.test(tf)) return "1D";
  if (/^weekly$/i.test(tf)) return "1W";
  if (/^monthly$/i.test(tf)) return "1M";
  if (/^hourly$/i.test(tf)) return "1H";
  return tf;
}

function AnchorReadout({
  bars,
  slots,
  focusedIdx,
  isDesktop,
  onPlace,
  onClear,
  onUndo,
  onReset,
}: {
  bars: string[];
  slots: (number | null)[];
  focusedIdx: number | null;
  isDesktop: boolean;
  onPlace: (i: number) => void;
  onClear: (i: number) => void;
  onUndo: () => void;
  onReset: () => void;
}) {
  const placedCount = slots.filter((x) => x !== null).length;
  const focusedDate = focusedIdx !== null ? bars[focusedIdx]?.slice(0, 10) : null;
  const canPlace = focusedIdx !== null;
  const nextEmpty = slots.findIndex((x) => x === null);
  return (
    <div className="step-row">
      <div className="step-pills">
        {STEP_LABELS.map((label, i) => {
          const idx = slots[i];
          const placed = idx !== null;
          const date = placed ? bars[idx]?.slice(0, 10) : null;
          const cls =
            "step-pill" +
            (placed ? " on" : i === nextEmpty ? " active" : "");
          // Desktop: pill click clears a placed slot so the next candle click
          // refills it. Mobile: pill click places the focused candle.
          const pillDisabled = isDesktop ? !placed : !canPlace;
          const pillClick = isDesktop
            ? () => onClear(i)
            : () => onPlace(i);
          return (
            <button
              key={label}
              className={cls}
              type="button"
              disabled={pillDisabled}
              onClick={pillClick}
            >
              <div className="step-num">{i + 1}</div>
              <div>
                <div className="step-label">{label}</div>
                <div className="muted small">{date ?? "—"}</div>
              </div>
            </button>
          );
        })}
      </div>
      <div className="step-actions">
        <button onClick={onUndo} disabled={!placedCount}>
          Undo
        </button>
        <button onClick={onReset} disabled={!placedCount}>
          Reset
        </button>
      </div>
      <div className="muted small">
        {isDesktop
          ? placedCount === 3
            ? "All anchors set — click a cell to clear it."
            : "Click a candle to fill the next cell."
          : canPlace
          ? `Focused: ${focusedDate} — tap a cell to place.`
          : "Tap a candle to focus, then tap a cell to place."}
      </div>
    </div>
  );
}

function DirectionRow({
  value,
  inferred,
  onChange,
}: {
  value: "bull" | "bear" | null;
  inferred: "bull" | "bear" | null;
  onChange: (v: "bull" | "bear") => void;
}) {
  return (
    <div className="dir-row">
      <span className="muted small">Direction</span>
      {(["bull", "bear"] as const).map((d) => {
        const isOn = (value ?? inferred) === d;
        return (
          <button
            key={d}
            className={"chip" + (isOn ? " chip-on" : "")}
            onClick={() => onChange(d)}
          >
            {d}
            {inferred === d && value === null ? " · auto" : ""}
          </button>
        );
      })}
    </div>
  );
}

function SymbolTypeahead({
  value,
  onChange,
}: {
  value: string;
  onChange: (sym: string) => void;
}) {
  const [text, setText] = useState(value);
  const [open, setOpen] = useState(false);
  const [debounced, setDebounced] = useState(value);
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Keep input text in sync if the parent picks a symbol some other way.
  useEffect(() => {
    setText(value);
  }, [value]);

  // Debounce keystrokes so we're not firing /search on every character.
  useEffect(() => {
    const id = setTimeout(() => setDebounced(text), 120);
    return () => clearTimeout(id);
  }, [text]);

  const resultsQ = useQuery({
    queryKey: ["symbols", debounced],
    queryFn: () => api.searchSymbols(debounced, 20),
    enabled: open,
    staleTime: 60_000,
  });

  const pick = (sym: string) => {
    onChange(sym);
    setText(sym);
    setOpen(false);
    // Dismiss the mobile keyboard.
    inputRef.current?.blur();
  };

  return (
    <div className="symbol-typeahead">
      <input
        ref={inputRef}
        type="text"
        value={text}
        placeholder="Search symbol…"
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 120)}
        onChange={(e) => {
          setText(e.target.value.toUpperCase());
          setOpen(true);
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            const top = resultsQ.data?.results?.[0];
            if (top) pick(top.symbol);
            else if (text.trim()) pick(text.trim().toUpperCase());
          } else if (e.key === "Escape") {
            setOpen(false);
          }
        }}
      />
      {open && (
        <div className="symbol-results">
          {resultsQ.isLoading && (
            <div className="symbol-row muted">Searching…</div>
          )}
          {!resultsQ.isLoading && (resultsQ.data?.results ?? []).length === 0 && (
            <div className="symbol-row muted">No matches</div>
          )}
          {resultsQ.data?.results?.map((r) => (
            <div
              key={r.symbol}
              className="symbol-row"
              onMouseDown={(e) => {
                e.preventDefault();
                pick(r.symbol);
              }}
            >
              <span className="symbol-tkr">{r.symbol}</span>
              <span className="symbol-name muted">{r.name}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function PreviewMetrics({ setup }: { setup: Setup }) {
  const cells: [string, string][] = [
    ["Leg move", `${(Number(setup.leg_move_pct) * 100).toFixed(1)}%`],
    ["Range height", `${(Number(setup.tr_height_leg_pct) * 100).toFixed(0)}% of leg`],
    ["Target", Number(setup.measured_move_target).toFixed(2)],
    ["Score", Number(setup.score).toFixed(1)],
  ];
  return (
    <div className="detail-grid" style={{ marginTop: 12 }}>
      {cells.map(([k, v]) => (
        <div key={k} className="detail-cell">
          <div className="k">{k}</div>
          <div className="v">{v}</div>
        </div>
      ))}
    </div>
  );
}
